import { useId, useRef, useState } from "react";

import { env } from "@/config/env";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";
import type {
  AdminAccessRequest,
  LiveScope,
  LiveScopeLocation,
} from "@/lib/api/admin-access-requests";
import { deskStudioUrl } from "@/pages/admin/desk-studio-url";
import { ApiErrorNotice } from "@/lib/api/portal-error-notice";
import type { PresentedApiError } from "@/lib/api/portal-error-copy";

const APPROVE_CONFIRMATION = "Approve";

type LiveApproveDialogProps = {
  request: AdminAccessRequest;
  submitting: boolean;
  error: PresentedApiError | null;
  onCancel: () => void;
  onConfirm: () => void;
};

function scopeLocations(scope: LiveScope): LiveScopeLocation[] {
  if (scope.access_scope === "namespaces") {
    return scope.namespaces.flatMap((namespace) => namespace.locations);
  }
  return scope.locations;
}

function scopeHasDemo(scope: LiveScope): boolean {
  return scopeLocations(scope).some((location) => location.demo);
}

function scopeBlocksApprove(scope: LiveScope): boolean {
  if (scope.access_scope === "namespaces") {
    return scope.namespaces.some((namespace) => namespace.status !== "resolved");
  }
  return scope.locations.some((location) => location.status !== "resolved");
}

function statusLabel(location: LiveScopeLocation): string | null {
  if (location.demo) {
    return "May be a sandbox location";
  }
  if (location.status === "not_found") {
    return "Not found";
  }
  if (location.status === "ambiguous") {
    return "More than one location matches this name";
  }
  return null;
}

function LocationRow({ location }: { location: LiveScopeLocation }) {
  const label = statusLabel(location);
  const title = location.name || location.requested_name || "Location";
  const deskUrl =
    location.status === "resolved" && location.branch_id && env.deskBaseUrl
      ? deskStudioUrl(env.deskBaseUrl, location.branch_id)
      : null;

  return (
    <li className="live-approve__row" data-testid="admin-live-approve-row">
      <div className="live-approve__row-main">
        <span className="live-approve__name">{title}</span>
        {location.namespace ? (
          <span className="live-approve__meta">{location.namespace}</span>
        ) : null}
        {location.branch_id ? (
          <span className="live-approve__meta">
            {deskUrl ? (
              <a
                href={deskUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="admin-live-approve-location-id"
              >
                {location.branch_id}
              </a>
            ) : (
              location.branch_id
            )}
          </span>
        ) : null}
        {location.active !== undefined ? (
          <span className="live-approve__meta">
            {location.active ? "Active" : "Inactive"}
          </span>
        ) : null}
        {label ? <span className="live-approve__status">{label}</span> : null}
      </div>
      {deskUrl ? (
        <a
          className="live-approve__details"
          href={deskUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="admin-live-approve-more-details"
        >
          More details
        </a>
      ) : null}
    </li>
  );
}

export function LiveApproveDialog({
  request,
  submitting,
  error,
  onCancel,
  onConfirm,
}: LiveApproveDialogProps) {
  const scope = request.live_scope;
  const [confirmation, setConfirmation] = useState("");
  const [locationsChecked, setLocationsChecked] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const confirmId = useId();
  const checkId = useId();
  const blocked = scope ? scopeBlocksApprove(scope) : true;
  const hasDemo = scope ? scopeHasDemo(scope) : false;
  const typedApprove =
    confirmation.trim().toLowerCase() === APPROVE_CONFIRMATION.toLowerCase();
  const canConfirm =
    typedApprove && locationsChecked && !blocked && !submitting;
  const namespaceScope = scope?.access_scope === "namespaces";

  useDialogFocusTrap(true, modalRef, onCancel);

  if (!scope) {
    return null;
  }

  return (
    <div className="review-modal-root" data-testid="admin-live-approve-modal">
      <button
        type="button"
        className="review-modal-backdrop"
        aria-label="Close confirmation"
        onClick={onCancel}
      />
      <div
        ref={modalRef}
        className="review-modal review-modal--live"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <h3 id={titleId} className="review-modal__title">
          Approve access request?
        </h3>
        <p className="review-modal__body">
          {namespaceScope
            ? "Are you sure you want to give this integrator access to all these namespaces?"
            : "Are you sure you want to give this integrator access to all these locations?"}
        </p>
        <p className="live-approve__heading">
          {namespaceScope ? "NAMESPACES" : "LOCATIONS"}
        </p>
        {namespaceScope ? (
          <ul className="live-approve__groups">
            {scope.namespaces.map((namespace) => (
              <li key={namespace.namespace}>
                <p className="live-approve__namespace">{namespace.namespace}</p>
                {namespace.status !== "resolved" ? (
                  <p
                    className="live-approve__status"
                    data-testid="admin-live-approve-row"
                  >
                    Not found
                  </p>
                ) : (
                  <ul className="live-approve__list">
                    {namespace.locations.map((location) => (
                      <LocationRow
                        key={location.branch_id || location.name}
                        location={location}
                      />
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <ul className="live-approve__list">
            {scope.locations.map((location) => (
              <LocationRow
                key={location.branch_id || location.requested_name}
                location={location}
              />
            ))}
          </ul>
        )}
        <label className="live-approve__check" htmlFor={checkId}>
          <input
            id={checkId}
            type="checkbox"
            data-testid="admin-live-approve-sandbox-check"
            checked={locationsChecked}
            onChange={(event) => {
              setLocationsChecked(event.target.checked);
            }}
          />
          <span>
            I have checked these locations and confirm they are not sandbox
            studios.
          </span>
        </label>
        <div className="review-modal__field">
          <label className="review-modal__label" htmlFor={confirmId}>
            TYPE APPROVE TO CONFIRM
          </label>
          <input
            id={confirmId}
            className="live-approve__input"
            data-testid="admin-live-approve-confirmation"
            value={confirmation}
            autoComplete="off"
            onChange={(event) => {
              setConfirmation(event.target.value);
            }}
          />
        </div>
        {hasDemo ? (
          <p
            className="review-modal__error"
            role="alert"
            data-testid="admin-live-approve-demo"
          >
            This name may be a sandbox location. It includes sandbox, demo,
            test, or a similar word.
          </p>
        ) : null}
        {blocked && !hasDemo ? (
          <p className="review-modal__error" role="alert">
            Every location must resolve before you can approve.
          </p>
        ) : null}
        {error ? (
          <ApiErrorNotice error={error} className="review-modal__error" />
        ) : null}
        <div className="review-modal__actions">
          <button
            type="button"
            className="review-modal__btn review-modal__btn--secondary"
            data-testid="admin-review-modal-cancel"
            disabled={submitting}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="review-modal__btn review-modal__btn--approve"
            data-testid="admin-review-modal-confirm"
            disabled={!canConfirm}
            onClick={onConfirm}
          >
            {submitting ? "Approving…" : "Approve request"}
          </button>
        </div>
      </div>
    </div>
  );
}
